import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from '../toast';
import {
  type ApiDate,
  type DateRange,
  defaultRangeDate,
  isPastDate,
  isValidRange,
  parseApiDate,
} from './api-date';

/** The subset of an infinite "available dates" query the range hook needs. */
export interface AvailableDatesQuery {
  dates: ApiDate[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => unknown;
}

interface UrlDateRangeOptions {
  /** Hook that loads the selectable dates for a (debounced) search term. Must be stable. */
  useDates: (search: string) => AvailableDatesQuery;
  messages: { title: string; missing: string; invalid: string };
}

const isApiDate = (v: string | null): v is string => !!v && !!parseApiDate(v);
type ExtraParams = Record<string, string | null>;

/**
 * Applied date range kept in the URL (`?startDate&endDate`, DD-MM-YYYY) so links and reloads keep
 * it. Without one, the production default is applied: yesterday, else the latest past day with
 * data. Pickers edit a draft that is only applied by `apply()` ("Daten abrufen").
 */
export function useUrlDateRange({ useDates, messages }: UrlDateRangeOptions) {
  const [params, setParams] = useSearchParams();
  const urlStart = params.get('startDate');
  const urlEnd = params.get('endDate');
  const applied = useMemo<DateRange | null>(
    () => (isApiDate(urlStart) && isApiDate(urlEnd) ? { start: urlStart, end: urlEnd } : null),
    [urlStart, urlEnd],
  );

  const [draft, setDraft] = useState<DateRange>({ start: urlStart ?? '', end: urlEnd ?? '' });
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const dates = useDates(debounced);
  const pastDates = useMemo(() => dates.dates.filter(isPastDate), [dates.dates]);

  // Keep the pickers in sync when the URL changes (back/forward, tabs).
  useEffect(() => {
    if (applied) setDraft(applied);
  }, [applied]);

  // The default day (yesterday, else the latest past day with data). Remembered from the last
  // unfiltered list, since a search narrows `dates` and would give a wrong default.
  const lastDefault = useRef<ApiDate | null>(null);
  if (!debounced && !dates.isLoading) lastDefault.current = defaultRangeDate(dates.dates);
  const defaultDay = lastDefault.current;

  // First visit without a range; a search-filtered list never seeds the default.
  const defaulted = useRef(false);
  useEffect(() => {
    if (applied || defaulted.current || debounced || dates.isLoading) return;
    const day = defaultRangeDate(dates.dates);
    if (!day) return;
    defaulted.current = true;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('startDate', day);
        next.set('endDate', day);
        return next;
      },
      { replace: true },
    );
  }, [applied, debounced, dates.isLoading, dates.dates, setParams]);

  /** Validates and applies the draft range; `extra` params are set (or removed when null). */
  const apply = (extra: ExtraParams = {}) => {
    if (!draft.start || !draft.end) {
      toast.error(messages.title, messages.missing);
      return false;
    }
    if (!isValidRange(draft)) {
      toast.error(messages.title, messages.invalid);
      return false;
    }
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('startDate', draft.start);
      next.set('endDate', draft.end);
      for (const [key, value] of Object.entries(extra)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
    return true;
  };

  const isDefault =
    !applied || !defaultDay || (applied.start === defaultDay && applied.end === defaultDay);

  /** Back to the default day; `extra` params are set (or removed when null) in the same step. */
  const reset = (extra: ExtraParams = {}) => {
    if (!defaultDay) return;
    setDraft({ start: defaultDay, end: defaultDay });
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('startDate', defaultDay);
      next.set('endDate', defaultDay);
      for (const [key, value] of Object.entries(extra)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  };

  return {
    applied,
    draft,
    setDraft,
    apply,
    reset,
    isDefault,
    params,
    setParams,
    availableDates: pastDates,
    datesQuery: dates,
    setSearch,
    searching: !!debounced && dates.isFetching && !dates.isFetchingNextPage,
  };
}
